'use client';

import {
	type ApiErrorResponse,
	EventIdentifier,
	FeatureTeamEvent,
	noop,
	NotificationService,
	TrackingEventType,
	useAppLoader,
	useCountdown,
	useDeviceId,
	useIntl,
	useServices,
	useSettings
} from '@capsicum/common';
import { BaseCodeInput, type BaseCodeInputRef, Button, Notification, useRouter } from '@capsicum/common-web';
import {
	type CodeInputError,
	getCodeInputError,
	type PhoneNumberFormattedMedium,
	supportSections,
	useCodeVerification,
	useOnboardingAnalytics,
	useRegistrationVerification,
	useSendVerificationCode,
	useSetSectionItemsLinks
} from '@capsicum/socios-core';
import { type FunctionComponent, type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { match } from 'ts-pattern';

import { useTriggerCaptcha } from '../../hooks';
import { Shell } from '../../ui';
import GeeTestContainer, { type GeeTestRef } from '../user-details/GeeTestContainer/GeeTestContainer';

type CodeVerificationProps = {
	formattedValues: PhoneNumberFormattedMedium;
	goToRegistration: (code: string) => void;
	reset: () => void;
	sendByEmail: () => Promise<void>;
	sendToRegistration?: boolean;
};

export const CodeVerification: FunctionComponent<CodeVerificationProps> = ({
	formattedValues,
	goToRegistration,
	reset,
	sendByEmail,
	sendToRegistration
}) => {
	const { $t } = useIntl();
	const captchaRef = useRef<GeeTestRef>(null);
	const { verifyAndLoginSms } = useCodeVerification();
	const [geetestReady, setGeetestReady] = useState(false);
	const { registrationVerificationSMS } = useRegistrationVerification();
	const appLoader = useAppLoader();
	const { replace, searchParams } = useRouter();
	const { analytics, setUserId } = useServices();
	const { sendRegistrationCodeVerifyEvent } = useOnboardingAnalytics();
	const inputCodeRef = useRef<BaseCodeInputRef>(null);
	const { sendWhatsappCodeByPhoneNumberToUser } = useSendVerificationCode();
	const triggerCaptcha = useTriggerCaptcha({ captcha: captchaRef.current });

	const {
		settings: { IsWhatsAppCodesEnabled }
	} = useSettings();
	const deviceUid = useDeviceId() as string;

	const sectionsWithLinks = useSetSectionItemsLinks(supportSections);

	const [codeInputError, setCodeInputError] = useState<CodeInputError>();
	const [code, setCode] = useState<string>('');
	const [alreadySentByEmail, setAlreadySentByEmail] = useState(false);
	const [alreadySentByWhatsapp, setAlreadySentByWhatsapp] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const reminderCountdown = useCountdown({
		immediate: true,
		minutes: 0,
		seconds: 60
	});

	const onSendByEmailHandler = useCallback(
		() =>
			sendByEmail()
				.then(() => {
					NotificationService.addNotification({
						component: (
							<Notification
								message={$t('codeVerification.emailSent', {
									email: formattedValues.email
								})}
								title={$t('codeVerification.emailSentTitle')}
								variant="success"
							/>
						)
					});
					reminderCountdown.reset();
					setAlreadySentByEmail(true);
				})
				.catch(noop),
		[sendByEmail, $t, formattedValues.email, reminderCountdown]
	);

	useEffect(() => {
		if (code.length === 6)
			(async () => {
				inputCodeRef.current?.blur();
				setIsSubmitLoading(true);
				try {
					// if user is registered, then login
					if (!sendToRegistration) {
						const loginResponse = await appLoader(
							verifyAndLoginSms({
								deviceName: 'webapp',
								deviceUid,
								generatedCode: code,
								mobileNumber: formattedValues.mobileNumber
							})
						);

						setUserId(loginResponse?.userId || '');
						analytics.sendEvent(TrackingEventType.track, EventIdentifier.LoginEnd, {
							action: 'login',
							feature_team: FeatureTeamEvent.OFT,
							status: 'success'
						});
						analytics.sendEvent(TrackingEventType.identify, loginResponse?.userId || '');
						replace(`/?${searchParams.toString()}`);
					}
					// if user is NOT registered, then go to registration
					else {
						const response = await appLoader(
							registrationVerificationSMS({
								generatedCode: code,
								mobileNumber: formattedValues.mobileNumber
							})
						);

						if (response.error) {
							sendRegistrationCodeVerifyEvent('failure');

							analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingSmsCode, {
								action: 'sign_up',
								feature_team: FeatureTeamEvent.OFT,
								status: 'failure'
							});
							setCodeInputError('generic');
						} else {
							sendRegistrationCodeVerifyEvent('success');

							analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingSmsCode, {
								action: 'sign_up',
								feature_team: FeatureTeamEvent.OFT,
								status: 'success'
							});
							goToRegistration(code);
						}
					}
				} catch (e) {
					inputCodeRef.current?.focus();
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingSmsCode, {
						action: 'login',
						feature_team: FeatureTeamEvent.OFT,
						status: 'failure'
					});
					const error: ApiErrorResponse = (e as any)?.response?.data;

					setCodeInputError(getCodeInputError(error));
				} finally {
					setIsSubmitLoading(false);
				}
			})();
	}, [
		formattedValues.mobileNumber,
		$t,
		verifyAndLoginSms,
		appLoader,
		replace,
		code,
		searchParams,
		sendToRegistration,
		registrationVerificationSMS,
		sendRegistrationCodeVerifyEvent,
		goToRegistration,
		analytics,
		deviceUid
	]);

	const onSendCodeToWhatsapp = useCallback(async () => {
		setAlreadySentByWhatsapp(true);

		try {
			await sendWhatsappCodeByPhoneNumberToUser({
				attempt: '0',
				captchaInformation: await triggerCaptcha(),
				countryCode: formattedValues.countryCode,
				mobileNumber: formattedValues.mobileNumber
			});

			NotificationService.addNotification({
				component: (
					<Notification
						message={$t('codeVerification.whatsAppSent')}
						title={$t('codeVerification.whatsAppSentTitle')}
						variant="success"
					/>
				)
			});
		} catch {
			NotificationService.addNotification({
				component: (
					<Notification
						message={$t('codeVerification.whatsAppSentError')}
						title={$t('codeVerification.whatsAppSentErrorTitle')}
						variant="error"
					/>
				)
			});
		}
	}, [$t, triggerCaptcha, formattedValues, sendWhatsappCodeByPhoneNumberToUser]);

	const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
			e.preventDefault();
		}
	}, []);

	const otherMethodsActions = useMemo(() => {
		const actions: Array<JSX.Element> = [];

		if (formattedValues.email) {
			actions.push(
				<Button
					color="neutral"
					disabled={alreadySentByEmail}
					testID="send-by-email"
					text={$t('auth.sendByEmail2')}
					variant="outlined"
					onClick={onSendByEmailHandler}
				/>
			);
		}

		if (IsWhatsAppCodesEnabled && !formattedValues.isAvailable && geetestReady) {
			actions.push(
				<Button
					color="neutral"
					disabled={alreadySentByWhatsapp}
					testID="send-by-whatsapp"
					text={$t('auth.sendByWhatsApp')}
					variant="outlined"
					onClick={onSendCodeToWhatsapp}
				/>
			);
		}

		return actions;
	}, [
		$t,
		geetestReady,
		formattedValues,
		alreadySentByEmail,
		onSendCodeToWhatsapp,
		onSendByEmailHandler,
		alreadySentByWhatsapp,
		IsWhatsAppCodesEnabled
	]);

	return (
		<Shell>
			<S.PageTitle>{$t('codeVerification.title')}</S.PageTitle>
			<S.Disclaimer>
				{$t('codeVerification.sentTo', {
					medium: <S.Medium>{formattedValues.mobileNumber}</S.Medium>
				})}
			</S.Disclaimer>
			<S.StyledCodeInput
				codeLength={6}
				disabled={isSubmitLoading}
				hasError={!!codeInputError}
				name="code"
				pattern="[0-9]*"
				ref={inputCodeRef}
				testID="login-code-input"
				type="tel"
				value={code}
				onChange={setCode}
				onKeyDown={handleKeyDown}
				onOvershoot={c => {
					setCode(c.substring(c.length - 1, c.length));
					setCodeInputError(undefined);
				}}
			/>
			{codeInputError && (
				<S.RequestNewCode>
					{match(codeInputError)
						.with('disabled-account', () =>
							$t<JSX.Element>('codeVerification.disabledAccount', {
								error: chunks => <S.RequestNewCodeAfterError>{chunks}</S.RequestNewCodeAfterError>,
								link: chunks => (
									<S.RequestNewCodeLink
										data-testid="login-contact-us-link"
										href={
											sectionsWithLinks
												.find(x => x.title === 'profile.support')
												?.items?.find(x => x.title === 'profile.contactUsHere')?.link
										}
										target="_blank"
									>
										{chunks}
									</S.RequestNewCodeLink>
								)
							})
						)
						.with('disabled-region', () => (
							<S.RequestNewCodeAfterError>
								{$t('codeVerification.disableWebAppRegion')}
							</S.RequestNewCodeAfterError>
						))
						.otherwise(() => (
							<S.RequestNewCodeAfterError>
								{$t('codeVerification.requestAnotherCodeAfterError')}
							</S.RequestNewCodeAfterError>
						))}
				</S.RequestNewCode>
			)}

			{reminderCountdown.seconds === undefined || reminderCountdown.seconds <= 0 ? (
				codeInputError === 'generic' ? (
					<S.RequestNewCodeBottom>
						<S.RequestNewCodeLink data-testid="login-send-again-link" onClick={() => reset()}>
							{$t('codeVerification.pleaseTryAgain')}
						</S.RequestNewCodeLink>
					</S.RequestNewCodeBottom>
				) : (
					<S.RequestNewCode $hasBottomMargin>
						{$t<JSX.Element>('codeVerification.requestAnotherCode', {
							link: chunks => (
								<S.RequestNewCodeLink data-testid="login-send-again-link" onClick={() => reset()}>
									{chunks}
								</S.RequestNewCodeLink>
							)
						})}
					</S.RequestNewCode>
				)
			) : (
				<S.LoginTimer>{reminderCountdown.seconds}</S.LoginTimer>
			)}

			{otherMethodsActions.length > 0 && (
				<S.OtherMethods>
					<S.OtherMethodsLabel>{$t('auth.welcome.codeInput.otherOptions')}</S.OtherMethodsLabel>
					<S.OtherMethodsActions>{otherMethodsActions}</S.OtherMethodsActions>
				</S.OtherMethods>
			)}

			<GeeTestContainer ref={captchaRef} onReady={() => setGeetestReady(true)} />
		</Shell>
	);
};

namespace S {
	export const InnerWrapper = styled.div`
		width: 100%;
		padding: ${({ theme }) => theme.spacings.xlarge} ${({ theme }) => theme.spacings.xxxlarge};
	`;

	export const PageTitle = styled.label`
		display: block;
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Disclaimer = styled.label`
		display: inline-block;
		${({ theme }) => theme.fonts.text.text3};
		margin: ${({ theme }) => theme.spacings.xxxsmall} 0 ${({ theme }) => theme.spacings.xlarge};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Medium = styled.span`
		${({ theme }) => theme.fonts.text.text1};
	`;

	export const RequestNewCode = styled.div<{ $hasBottomMargin?: boolean }>`
		text-align: center;
		margin-top: ${({ theme }) => theme.spacings.xlarge};
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ $hasBottomMargin, theme }) => ($hasBottomMargin ? theme.spacings.large : 0)};
	`;

	export const RequestNewCodeBottom = styled(RequestNewCode)`
		margin-top: 0;
		margin-bottom: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const RequestNewCodeLink = styled.a`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		${({ theme }) => theme.fonts.text.text4};
	`;

	export const RequestNewCodeAfterError = styled.span`
		text-align: center;
		margin-top: ${({ theme }) => theme.spacings.large};
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.error.enabled};
	`;

	export const LoginTimer = styled.p`
		text-align: center;
		margin-top: ${({ theme }) => theme.spacings.xlarge};
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.main.disabled};
		margin-bottom: 0;
	`;

	export const StyledCodeInput = styled(BaseCodeInput)`
		justify-content: center;
	`;

	export const OtherMethods = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const OtherMethodsLabel = styled.p`
		margin: 0;
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const OtherMethodsActions = styled.div`
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;
}
